import React from 'react';
import styles from './StrategyCard.module.css';
import { formatAdminDate } from '../../../utils/datetime';

const getRelativeTime = (dateString) => {
  if (!dateString) return 'Never';
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return formatAdminDate(date);
};

const StrategyCard = ({ strategy, onToggle, onEdit, onDelete, onClick }) => {
  if (!strategy) return null;

  const {
    name = 'Untitled Strategy',
    symbol = 'N/A',
    symbols: rawSymbols,
    timeframe = '1m',
    is_active = false,
    action = 'BUY',
    conditions = [],
    // `last_activity_at` is the new field from findAll — the most
    // recent trigger across all of this strategy's symbols. The
    // legacy `last_triggered` destructure used to refer to a field
    // that never existed on the response (the actual column is
    // `last_triggered_at` and is now inert post-multi-symbol), so
    // every card showed "Last triggered: Never" regardless of
    // reality. Falling back to the legacy `last_triggered_at` for
    // any pre-Phase-1 response that hasn't been redeployed.
    last_activity_at,
    last_triggered_at: legacyLastTriggeredAt,
  } = strategy;
  const lastTriggeredAt = last_activity_at || legacyLastTriggeredAt || null;

  // Multi-symbol display. Prefer the `symbols` array from the
  // backend; for older / legacy responses that only have the
  // singular `symbol` string, wrap it into a one-element array so
  // the rendering code below has a single shape to work with.
  // If neither is present, show 'N/A' as a placeholder so the
  // card layout doesn't collapse.
  const symbols = Array.isArray(rawSymbols) && rawSymbols.length > 0
    ? rawSymbols
    : (symbol && symbol !== 'N/A' ? [symbol] : ['N/A']);

  // Show up to 3 chips inline; the rest collapse into "+N more"
  // (the same overflow pattern conditions already use below).
  const visibleSymbols = symbols.slice(0, 3);
  const extraSymbolsCount = Math.max(0, symbols.length - 3);
  const extraSymbolsLabel = symbols.slice(3).join(', ');

  const isActive = is_active;
  const isPaused = false; // future: strategy.status === 'paused'
  const actionUpper = String(action).toUpperCase();

  const getStatusConfig = () => {
    if (isActive) return { dotClass: styles.dotActive, label: 'Active' };
    if (isPaused) return { dotClass: styles.dotPaused, label: 'Paused' };
    return { dotClass: styles.dotInactive, label: 'Inactive' };
  };

  const statusConfig = getStatusConfig();
  const displayConditions = conditions.slice(0, 2);

  const handleBacktest = () => {
    if (onClick) onClick(strategy);
  };

  const handleToggle = () => {
    if (onToggle) onToggle(strategy);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit) onEdit(strategy);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete(strategy);
  };

  return (
    <div
      className={styles.card}
      role="article"
    >
      {/* Header */}
      <div className={styles.cardHeader}>
        <h3 className={styles.strategyName}>{name}</h3>
        <div className={styles.headerRight} data-card-toggle onClick={(e) => e.stopPropagation()}>
          <div className={styles.statusBadge}>
            <span className={`${styles.statusDot} ${statusConfig.dotClass}`} />
            <span className={styles.statusText}>{statusConfig.label}</span>
          </div>
          <label className={styles.toggleSwitch}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={handleToggle}
              className={styles.toggleInput}
              disabled={!onToggle}
            />
            <span className={styles.toggleTrackSmall}>
              <span className={styles.toggleThumbSmall} />
            </span>
          </label>
        </div>
      </div>

      {/* Badges */}
      <div className={styles.badgeRow}>
        {visibleSymbols.map((s) => (
          <span key={s} className={styles.symbolBadge}>{s}</span>
        ))}
        {extraSymbolsCount > 0 && (
          <span
            className={styles.moreSymbols}
            title={extraSymbolsLabel}
          >
            +{extraSymbolsCount} more
          </span>
        )}
        <span className={styles.timeframeBadge}>{timeframe || '1m'}</span>
        <span
          className={`${styles.actionBadge} ${
            actionUpper === 'BUY' ? styles.actionBuy : styles.actionSell
          }`}
        >
          {actionUpper}
        </span>
      </div>

      {/* Conditions Preview */}
      {displayConditions.length > 0 && (
        <div className={styles.conditionsPreview}>
          {displayConditions.map((cond, i) => (
            <span key={i} className={styles.conditionPill}>
              {cond.variable || cond.category} {cond.operator} {cond.value}
            </span>
          ))}
          {conditions.length > 2 && (
            <span className={styles.moreConditions}>
              +{conditions.length - 2} more
            </span>
          )}
        </div>
      )}

      {/* Last Triggered */}
      <div className={styles.lastTriggered}>
        <span className="material-symbols-outlined">schedule</span>
        <span>Last triggered: {getRelativeTime(lastTriggeredAt)}</span>
      </div>

      {/* Footer */}
      <div className={styles.cardFooter}>
        <button
          className={styles.backtestBtn}
          onClick={handleBacktest}
          title="View details & backtest"
        >
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>query_stats</span>
          Backtest
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_forward</span>
        </button>
        <div className={styles.footerActions} data-card-actions>
          {onEdit && (
            <button
              className={styles.iconButton}
              onClick={handleEdit}
              title="Edit strategy"
              aria-label="Edit strategy"
            >
              <span className="material-symbols-outlined">edit</span>
            </button>
          )}
          {onDelete && (
            <button
              className={`${styles.iconButton} ${styles.deleteBtn}`}
              onClick={handleDelete}
              title="Delete strategy"
              aria-label="Delete strategy"
            >
              <span className="material-symbols-outlined">delete</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StrategyCard;
